import React from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import './Pagination.scss';

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  className = '',
  showEllipsis = true,
  maxVisiblePages = 5,
  // New props for items per page
  itemsPerPage,
  order_by,
  onItemsPerPageChange,
  itemsPerPageOptions = [24, 48, 96],
  totalItems,
  showItemsPerPage = false,
  // New props for order_by
  onOrderByChange,
  orderByOptions,
  showOrderBy = false
}) => {
  const { t } = useLanguage();

  const finalOrderByOptions = orderByOptions || [];

  if (totalPages <= 1 && !showItemsPerPage && !showOrderBy) {
    return null;
  }

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    if (onItemsPerPageChange) {
      onItemsPerPageChange(newItemsPerPage);
    }
  };

  const handleOrderByChange = (e) => {
    const newOrderBy = e.target.value;
    if (onOrderByChange) {
      onOrderByChange(newOrderBy);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);

    for (let i = 1; i <= totalPages; i++) {
      // Show first page, last page, current page, and pages around current
      const showPage = i === 1 ||
                      i === totalPages ||
                      Math.abs(i - currentPage) <= halfVisible;

      if (!showPage) {
        // Show ellipsis for gaps
        if (i === 2 && currentPage > halfVisible + 2) {
          pages.push(
            <span key={`ellipsis-${i}`} className="pagination-ellipsis">
              ...
            </span>
          );
        }
        if (i === totalPages - 1 && currentPage < totalPages - halfVisible - 1) {
          pages.push(
            <span key={`ellipsis-${i}`} className="pagination-ellipsis">
              ...
            </span>
          );
        }
        continue;
      }

      pages.push(
        <button
          key={i}
          className={`pagination-number ${i === currentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
          aria-label={`${t('pagination.goToPage')} ${i}`}
          aria-current={i === currentPage ? 'page' : undefined}
        >
          {i}
        </button>
      );
    }

    return pages;
  };

  const startItem = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className={`pagination-container ${className}`}>
      {/* Top controls row */}
      <div className="pagination-controls">
        {/* Order by selector */}
        {showOrderBy && (
          <div className="pagination-order-by">
            <label htmlFor="order-by" className="order-by-label">
              {t('pagination.sortBy')}:
            </label>
            <select
              id="order-by"
              value={order_by}
              onChange={handleOrderByChange}
              className="order-by-select"
            >
              {finalOrderByOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Items info */}
      {totalItems > 0 && (
        <div className="pagination-info">
          <span className="pagination-items-info">
            {t('pagination.showing')} {startItem}-{endItem} {t('common.of')} {totalItems} {t('pagination.items')}
          </span>
        </div>
      )}

      {/* Navigation controls */}
      {totalPages > 1 && (
        <nav className="pagination" role="navigation" aria-label="Pagination">
          {/* Items per page selector intégré */}
          {showItemsPerPage && (
            <div className="pagination-items-per-page-inline">
              <select
                id="items-per-page-inline"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="items-per-page-select"
                title={t('pagination.itemsPerPage')}
              >
                {itemsPerPageOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            className="pagination-button pagination-prev"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label={t('pagination.goToPreviousPage')}
          >
            {t('pagination.previous')}
          </button>

          <div className="pagination-numbers">
            {showEllipsis ? renderPageNumbers() : (
              Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={`pagination-number ${page === currentPage ? 'active' : ''}`}
                  onClick={() => handlePageChange(page)}
                  aria-label={`${t('pagination.goToPage')} ${page}`}
                  aria-current={page === currentPage ? 'page' : undefined}
                >
                  {page}
                </button>
              ))
            )}
          </div>

          <button
            className="pagination-button pagination-next"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label={t('pagination.goToNextPage')}
          >
            {t('pagination.next')}
          </button>
        </nav>
      )}
    </div>
  );
};

export default Pagination;